import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getData, getDataById, getcountries, getpaymentType, getseries, getsubdomains } from '../../../services/management/paymentReceipt/paymentReceiptGeneratedServices';
import { Modal, Button, Table } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';

// ── Parse remarks stored as JSON string or array ──────────────────────────────
const parseRemarksHistory = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch (_) {}
  return [];
};

// ── Format ISO → "22 Jun 2026, 10:30 AM" ─────────────────────────────────────
const formatTimestamp = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const PaymentReceiptGenerated = () => {
  const [dataList, setDataList] = useState([]);
  const [form, setForm] = useState({ 
    id: '',
    accommodation : '',
    accommodation_status : '',
    billing_address : '',
    billing_countries : '',
    billing_email : '',
    billing_full_name : '',
    billing_phone_no : '',
    createdAt : '',
    currency_type : '',
    date_of_payment : '',
    event_date : '',
    event_title : '',
    event_type : '',
    no_of_ticket : '',
    occupancy_type : '',
    payment_id : '',
    payment_type : '',
    participant_type: '',
    pdf_status : '',
    receipt_date : '',
    refund_status : '',
    reg_amount : '',
    reg_amount_usd : '',
    reg_category : '',
    reg_type : '',
    series : '',
    shipping : '',
    shipping_address : '',
    shipping_countries : '',
    shipping_email : '',
    shipping_full_name : '',
    shipping_phone_no : '',
    sub_domain : '',
    subject : '',
    total_no_of_nights : '',
    transaction_id : '',
    updatedAt : ''
  });
  
  const [showViewModal, setShowViewModal]   = useState(false);
  const [remarksHistory, setRemarksHistory] = useState([]);   // ← new

  const [searchText, setSearchText]           = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimeoutRef                    = useRef(null);
  const [currentPage, setCurrentPage]         = useState(1);
  const [loading, setLoading]                 = useState(true);

  // Filter States
  const [startDate, setStartDate]           = useState('');
  const [endDate, setEndDate]               = useState('');
  const [countryFilter, setCountryFilter]   = useState('');
  const [seriesFilter, setSeriesFilter]     = useState('');
  const [subDomainFilter, setSubDomainFilter] = useState('');
  const [dropdownData, setDropdownData]     = useState({
    countries: [], paymentType: [], subdomains: [], series: []
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows]     = useState(0);

  // ── Debounce ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1);
    }, 600);
    return () => clearTimeout(debounceTimeoutRef.current);
  }, [searchText]);

  // ── Fetch list ────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (subDomainFilter) filters.domain   = subDomainFilter;
      if (countryFilter)   filters.country  = countryFilter;

      const params = {
        pdf_status: 1,
        page: currentPage,
        limit: rowsPerPage,
        search: debouncedSearch,
        filters,
        startDate: startDate || null,
        endDate:   endDate   || null,
      };

      const response = await getData(params);
      console.log('Fetched Data:', response);
      setDataList(response.rows);
      setTotalRows(response.count);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, debouncedSearch, subDomainFilter, countryFilter, startDate, endDate]);

  // ── Fetch dropdowns ───────────────────────────────────────────────────────
  const fetchDropdownData = useCallback(async () => {
    try {
      const [country, payment, subdomains, series] = await Promise.all([
        getcountries(), getpaymentType(), getsubdomains(), getseries()
      ]);

      const uniqueSeries = [...new Set(series.map(item => item.series))].map(s => ({ series: s }));

      setDropdownData({ countries: country, paymentType: payment, subdomains, series: uniqueSeries });
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  }, []);

  useEffect(() => { fetchDropdownData(); }, [fetchDropdownData]);
  useEffect(() => { fetchData();         }, [fetchData]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const navigate = useNavigate();

  const pdfModal = async (type, row) => {
    try {
      const data = await getDataById({ id: row.id });
      if (data) navigate('/paymentReceiptPdf', { state: { data } });
    } catch (error) {
      console.error('Error fetching data by ID:', error);
    }
  };

  // ── Open view modal — parse remarks history ───────────────────────────────
  const openViewModal = (data = {
    id: '', accommodation: '', accommodation_status: '', billing_address: '',
    billing_countries: '', billing_email: '', billing_full_name: '',
    billing_phone_no: '', createdAt: '', currency_type: '', date_of_payment: '',
    event_date: '', event_title: '', event_type: '', no_of_ticket: '',
    occupancy_type: '', payment_id: '', payment_type: '', participant_type: '',
    pdf_status: '', receipt_date: '', refund_status: '', reg_amount: '',
    reg_amount_usd: '', reg_category: '', reg_type: '', series: '',
    shipping: '', shipping_address: '', shipping_countries: '', shipping_email: '',
    shipping_full_name: '', shipping_phone_no: '', sub_domain: '', subject: '',
    total_no_of_nights: '', transaction_id: '', updatedAt: '',
  }) => {
    setForm(data);
    setRemarksHistory(parseRemarksHistory(data.remarks));  // ← parse here
    setShowViewModal(true);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const handleSearchChange   = (e) => { setSearchText(e.target.value); setCurrentPage(1); };
  const handlePageChange     = (page) => setCurrentPage(page);
  const handlePerRowsChange  = (newPerPage, page) => { setRowsPerPage(newPerPage); setCurrentPage(page); };

  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode?.toUpperCase()) {
      case 'USD': return '$';  case 'EUR': return '€';
      case 'INR': return '₹';  case 'GBP': return '£';
      case 'RUB': return '₽';  case 'THB': return '฿';
      default:    return currencyCode || '';
    }
  };

  const getCountryName     = (code) => dropdownData.countries.find(c => c.code === code)?.name  ?? code;
  const getPaymentTypeName = (code) => dropdownData.paymentType.find(c => c.code === code)?.type ?? code;

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    { name: '#', selector: (row, index) => (currentPage - 1) * 10 + (index + 1), sortable: false, width: '60px' },
    { name: 'Date of Payment', selector: (row) => row.date_of_payment, sortable: true },
    {
      name: 'Series',
      selector: (row) => {
        const date   = new Date(row.series);
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${months[date.getMonth()]} ${date.getFullYear()}`;
      },
      sortable: true,
    },
    { name: 'Sub domain', selector: (row) => row.sub_domain,       sortable: true },
    { name: 'Name',       selector: (row) => row.billing_full_name, sortable: true },
    { name: 'Country',    selector: (row) => getCountryName(row.billing_countries), sortable: true },
    {
      name: 'Reg Amount',
      selector: (row) => (
        <b className="fs-4">{getCurrencySymbol(row.currency_type)} {row.reg_amount}</b>
      ),
      sortable: true,
    },
    { name: 'Event Type', selector: (row) => row.event_type, sortable: true },
    {
      name: 'Actions',
      cell: (row) => (
        <div>
          <Button variant="primary" size="sm" onClick={() => openViewModal(row)} className="me-2">
            <i className="align-middle far fa-fw fa-eye"></i>
          </Button>
          <Button variant="danger" size="sm" onClick={() => pdfModal('edit', row)}>
            <i className="align-middle far fa-fw fa-file-pdf"></i>PDF
          </Button>
        </div>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />

        {/* Filters */}
        <div className="row m-3 d-flex flex-column gap-3">
          <div className="d-flex gap-2">
            <div className="col-md-2">
              <span>Series</span>
              <select className="form-control" value={seriesFilter} onChange={(e) => setSeriesFilter(e.target.value)}>
                <option value="">All Series</option>
                {Array.from(new Set(dataList.map(item => item.series))).map((series, idx) => (
                  <option key={idx} value={series}>{series}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span>Sub Domain</span>
              <select className="form-control" value={subDomainFilter} onChange={(e) => setSubDomainFilter(e.target.value)}>
                <option value="">All Subdomains</option>
                {Array.from(new Set(dataList.map(item => item.sub_domain))).map((subDomain, idx) => (
                  <option key={idx} value={subDomain}>{subDomain}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span>Countries</span>
              <select className="form-control" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
                <option value="">All Countries</option>
                {dropdownData.countries.map((country) => (
                  <option key={country.code} value={country.code}>{country.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span>Start Date</span>
              <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <span>End Date</span>
              <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '300px' }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className="fs-4">PDF Generation / Payment Receipt / Generated Receipt</p>
                </div>
              </div>

              <DataTable
                columns={columns}
                data={dataList}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={rowsPerPage}
                paginationRowsPerPageOptions={[10, 30, 50, 100, 500]}
                onChangePage={handlePageChange}
                paginationDefaultPage={currentPage}
                onChangeRowsPerPage={handlePerRowsChange}
                highlightOnHover
                responsive
                striped
                subHeader
                subHeaderComponent={
                  <input className="form-control" type="text" placeholder="Search"
                    value={searchText} onChange={handleSearchChange} />
                }
              />

              {/* ── View Modal ── */}
              <Modal className="modal-lg" backdrop="static" show={showViewModal} onHide={() => setShowViewModal(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>View</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                  <Table striped bordered hover responsive className="table-sm">
                    <thead>
                      <tr><th className="bg-secondary text-white" colSpan={2}>Payment Receipt Details</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Event Type</td>      <td>{form.event_type}</td></tr>
                      <tr><td>Receipt Date</td>     <td>{form.receipt_date}</td></tr>
                      <tr><td>Payment ID</td>       <td>{form.payment_id}</td></tr>
                      <tr><td>Participant Type</td> <td>{form.participant_type}</td></tr>
                    </tbody>

                    <thead>
                      <tr><th className="bg-secondary text-white" colSpan={2}>Billing Address</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Full Name</td> <td>{form.billing_full_name}</td></tr>
                      <tr><td>Email</td>     <td>{form.billing_email}</td></tr>
                      <tr><td>Phone No</td>  <td>{form.billing_phone_no}</td></tr>
                      <tr><td>Address</td>   <td>{form.billing_address}</td></tr>
                      <tr><td>Country</td>   <td>{getCountryName(form.billing_countries)}</td></tr>
                    </tbody>

                    <thead>
                      <tr><th className="bg-secondary text-white" colSpan={2}>Shipping Address</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Full Name</td> <td>{form.shipping_full_name}</td></tr>
                      <tr><td>Email</td>     <td>{form.shipping_email}</td></tr>
                      <tr><td>Phone No</td>  <td>{form.shipping_phone_no}</td></tr>
                      <tr><td>Address</td>   <td>{form.shipping_address}</td></tr>
                      <tr><td>Country</td>   <td>{getCountryName(form.shipping_countries)}</td></tr>
                    </tbody>

                    <thead>
                      <tr><th className="bg-secondary text-white" colSpan={2}>Payment Description</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Date of Payment</td>       <td>{form.date_of_payment}</td></tr>
                      <tr><td>Mode of Payment</td>       <td>{getPaymentTypeName(form.payment_type)}</td></tr>
                      <tr><td>Currency</td>              <td>{form.currency_type}</td></tr>
                      <tr><td>Registration Amount</td>   <td>{form.reg_amount}</td></tr>
                      <tr><td>Registration Type</td>     <td>{form.reg_type}</td></tr>
                      <tr><td>Registration Category</td> <td>{form.reg_category}</td></tr>
                      <tr><td>No. of Ticket</td>         <td>{form.no_of_ticket}</td></tr>
                      {form.occupancy_type && (
                        <tr><td>Occupancy</td>     <td>{form.occupancy_type}</td></tr>
                      )}
                      {form.total_no_of_nights && (
                        <tr><td>No. of Nights</td> <td>{form.total_no_of_nights}</td></tr>
                      )}
                    </tbody>
                  </Table>

                  {/* ── Remarks History (read-only, shown only when entries exist) ── */}
                  {remarksHistory.length > 0 && (
                    <div className="mt-3">
                      <p className="fs-5 text-dark fw-semibold mb-2">
                        Remarks History
                        <span className="badge bg-secondary ms-2" style={{ fontSize: '0.72rem' }}>
                          {remarksHistory.length}
                        </span>
                      </p>
                      <div
                        className="border rounded"
                        style={{ maxHeight: '220px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}
                      >
                        {[...remarksHistory].reverse().map((entry, idx) => (
                          <div
                            key={idx}
                            className="px-3 py-2 border-bottom"
                            style={{ borderLeft: '3px solid #0d6efd' }}
                          >
                            <div className="d-flex justify-content-between align-items-baseline">
                              <small className="text-muted d-block mb-1">
                                🕒 {formatTimestamp(entry.timestamp)}
                              </small>
                              {entry.user && (
                                <span className="badge bg-info text-dark" style={{ fontSize: '0.72rem' }}>
                                  {entry.user}
                                </span>
                              )}
                            </div>
                            <p className="mb-0 text-dark" style={{ fontSize: '0.9rem' }}>
                              {entry.remark}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Modal.Body>
              </Modal>
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentReceiptGenerated;
